import type {
    SketchConstraintFormulaCompileContext,
    SketchConstraintFormulaCompileResult,
    SketchConstraintFormulaCompiler,
} from '../../../../types/sketch/solver/SketchConstraintFormulaCompiler';

/**
 * 固定关系结构编译器。
 *
 * Fix 不需要额外残差方程，而是在参数注册阶段把目标基础参数移出自由参数向量。
 * 本编译器负责验证 island 构建器已经完成该结构编译，防止 Fix 被当成空操作放行。
 */
export class SketchFixedConstraintFormulaCompiler implements SketchConstraintFormulaCompiler {
    /** 固定关系类型。 */
    public readonly relationKinds = ['Fix'] as const;

    /**
     * 验证 Fix 目标已经转为固定参数。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    public compile(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        if (context.constraintState.relationKind !== 'Fix') {
            return 'not-applicable';
        }

        const targetPointIds = context.constraintState.targetPointIds
            .filter((pointId) => context.pointHandles.has(pointId));
        const targetEntityIds = context.resolveDirectEntityIds();

        if (targetPointIds.length === 0 && targetEntityIds.length === 0) {
            context.reportUnsupported('固定关系没有可参数化的目标');
            return 'rejected';
        }

        const unfixedPointIds = targetPointIds
            .filter((pointId) => !context.fixedPointIds.has(pointId));

        if (unfixedPointIds.length > 0) {
            context.reportUnsupported(`固定关系存在未冻结参数的目标点：${unfixedPointIds.join('、')}`);
            return 'rejected';
        }

        const unfixedEntityIds = targetEntityIds
            .filter((entityId) => !context.fixedEntityIds.has(entityId));

        if (unfixedEntityIds.length > 0) {
            context.reportUnsupported(`固定关系存在未冻结基础参数的实体：${unfixedEntityIds.join('、')}`);
            return 'rejected';
        }

        return 'compiled';
    }
}
